// Typed client for /v1/* endpoints.

import type { CheckResponse, UploadSuccess, UploadFailure } from "./types";
import type { AppConfig } from "../state/types";

const API_BASE: string = import.meta.env.VITE_API_BASE ?? "http://localhost:8000";

export class MaintenanceError extends Error {
  constructor() {
    super("maintenance");
    this.name = "MaintenanceError";
  }
}

export class ApiHttpError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`http ${status}`);
    this.name = "ApiHttpError";
  }
}

type RequestInitWithAuth = RequestInit & { authToken?: string };

async function request<T>(path: string, init: RequestInitWithAuth = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.authToken) headers.set("Authorization", `Bearer ${init.authToken}`);
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const bodyText = await res.text();
  const body = bodyText ? safeJson(bodyText) : null;
  if (!res.ok) {
    if (res.status === 500 && body && typeof body === "object" && (body as { error?: string }).error === "maintenance") {
      throw new MaintenanceError();
    }
    throw new ApiHttpError(res.status, body);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try { return JSON.parse(text); } catch { return text; }
}

export function apiBase(): string { return API_BASE; }

export function check(uid: string, winnerId?: number, token?: string): Promise<CheckResponse> {
  return request<CheckResponse>("/v1/check", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ uid, winnerId: winnerId ?? null, token: token ?? null }),
  });
}

export function getConfig(): Promise<AppConfig> {
  return request<AppConfig>("/v1/config");
}

// ---------------------------------------------------------------------------
// Game session
// ---------------------------------------------------------------------------

export type GameStartInput = {
  uid: string;
  cupId: number | null;
  isMale: boolean | null;
  useCustomFace: boolean | null;
  winnerId?: number | null;
  token?: string | null;
};

/** Tạo session khi user chọn xong ly mì + giới tính. */
export function gameStart(input: GameStartInput): Promise<{ session_id: string }> {
  return request<{ session_id: string }>("/v1/game/start", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      uid: input.uid,
      cup_id: input.cupId,
      is_male: input.isMale,
      use_custom_face: input.useCustomFace,
      winnerId: input.winnerId ?? null,
      token: input.token ?? null,
    }),
  });
}

/** Cập nhật session sau khi user chọn nhân vật (template/custom). */
export function gameSessionUpdate(signature: string, useCustomFace: boolean): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/v1/game/session", {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ signature, use_custom_face: useCustomFace }),
  });
}

/** Bấm Tiếp tục sau hướng dẫn → bắt đầu tính giờ chơi. */
export function gameReady(signature: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/v1/game/ready", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ signature }),
  });
}

/** Kết thúc game — bấm X hoặc hết giờ. */
export function gameFinish(signature: string, playTimeS: number): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/v1/game/finish", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ signature, play_time_s: playTimeS }),
  });
}

// ---------------------------------------------------------------------------
// Session (default face)
// ---------------------------------------------------------------------------

export type SessionInput = {
  uid: string;
  isMale: boolean;
  defaults: string[];
  sessionId?: string | null;
};

export function recordSession(input: SessionInput): Promise<{ recorded: string }> {
  return request<{ recorded: string }>("/v1/session", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      uid: input.uid,
      isMale: input.isMale,
      defaults: input.defaults,
      session_id: input.sessionId ?? null,
    }),
  });
}

// ---------------------------------------------------------------------------
// Play report
// ---------------------------------------------------------------------------

export type PlayReportInput = {
  winnerId: number;
  userId: string;
  signature?: string | null;
};

export type PlayReportResult = {
  reported: boolean;
  duplicate: boolean;
  mock: boolean;
  status?: string;
  message?: string;
};

export function reportPlay(input: PlayReportInput): Promise<PlayReportResult> {
  return request<PlayReportResult>("/v1/play-report", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      winner_id: input.winnerId,
      user_id: input.userId,
      signature: input.signature ?? null,
    }),
  });
}

// ---------------------------------------------------------------------------
// Upload
// ---------------------------------------------------------------------------

export type UploadInput = {
  uid: string;
  isMale?: boolean;
  sessionId?: string | null;
  neutral?: Blob;
  happy?: Blob;
  sad?: Blob;
};

export async function upload(input: UploadInput): Promise<UploadSuccess | UploadFailure> {
  const form = new FormData();
  form.set("uid", input.uid);
  if (input.isMale !== undefined) form.set("isMale", input.isMale ? "true" : "false");
  if (input.sessionId != null) form.set("session_id", input.sessionId);
  if (input.neutral) form.set("neutral", input.neutral, "neutral.png");
  if (input.happy) form.set("happy", input.happy, "happy.png");
  if (input.sad) form.set("sad", input.sad, "sad.png");

  const res = await fetch(`${API_BASE}/v1/upload`, { method: "POST", body: form });
  const body = await res.json().catch(() => null);
  if (res.status === 200) return body as UploadSuccess;
  if (res.status === 422) return body as UploadFailure;
  if (res.status === 500 && body && body.error === "maintenance") throw new MaintenanceError();
  throw new ApiHttpError(res.status, body);
}
